import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, FileCheck2, ShieldCheck } from "lucide-react";
import { ProviderShell } from "@/components/provider/ProviderShell";
import { api, formatApiError } from "@/lib/api";

const Rows = ({ rows, testId }) => {
  const visible = rows.filter(([, value]) => value);
  if (!visible.length) return null;
  return <dl className="provider-assignment-rows" data-testid={testId}>{visible.map(([label, value, changed]) => <div className={changed ? "is-changed" : ""} key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>;
};

const contact = (c) => [c?.name, c?.phone, c?.email].filter(Boolean).join(" · ");

async function download(documentId, filename) {
  const response = await api.get(`/providers/me/client-documents/${documentId}/download`, { responseType: "blob" });
  const href = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

export default function ProviderAssignmentPage() {
  const { assignmentId } = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/providers/me/assignments/${assignmentId}`).then((r) => setPage(r.data)).catch((e) => setError(formatApiError(e))), [assignmentId]);
  useEffect(() => { load(); }, [load]);

  async function confirm() {
    setBusy(true); setError("");
    try { setPage((await api.post(`/providers/me/assignments/${assignmentId}/confirm`)).data); } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  }
  async function acknowledge(document) {
    setError("");
    try { await api.post(`/providers/me/client-documents/${document.id}/acknowledge`); load(); } catch (e) { setError(formatApiError(e)); }
  }

  const s = page?.snapshot;
  const changed = new Set((page?.changes || []).map((c) => c.key));
  return (
    <ProviderShell back="/provider-portal/file/assignments" eyebrow="YOUR ASSIGNMENT" testId="provider-assignment-page" title={s ? `${s.role} at ${s.client_name}` : "Assignment"}>
      {error && <p className="form-message error-message" data-testid="provider-assignment-error">{error}</p>}
      {!page && !error && <p className="projection-loading">Loading…</p>}
      {page && (
        <>
          <section className={`provider-assignment-confirm ${page.needs_confirmation ? "is-waiting" : "is-confirmed"}`} data-testid="provider-assignment-confirm">
            {page.needs_confirmation ? (
              <>
                <p>{page.changes.length ? "Your assignment details changed — please review the highlighted items and confirm again." : "Please read everything below and confirm so TAG knows you have it."}</p>
                <button className="primary-button" data-testid="provider-assignment-confirm-button" disabled={busy} type="button" onClick={confirm}><CheckCircle2 size={16} /> {busy ? "Confirming…" : "Confirm assignment"}</button>
              </>
            ) : (
              <p data-testid="provider-assignment-confirmed"><CheckCircle2 size={16} /> Confirmed {new Date(page.confirmed_at).toLocaleString()} · version {page.version}</p>
            )}
          </section>
          {page.changes.length > 0 && (
            <section className="provider-assignment-changes" data-testid="provider-assignment-changes">
              <h2>What changed</h2>
              <ul>{page.changes.map((c) => <li key={c.key}><strong>{c.label}</strong>: {c.after || "removed"}{c.before ? <small> (was {c.before})</small> : null}</li>)}</ul>
            </section>
          )}
          <p className="provider-assignment-intro" data-testid="provider-assignment-intro">{s.intro}</p>
          <section className="provider-assignment-section"><h2>Assignment</h2>
            <Rows testId="provider-assignment-basics" rows={[["Role", s.role], ["Start date", s.start_label, changed.has("start_date")], ["End date", s.end_label, changed.has("end_date")], ["Schedule", <>{s.schedule_lines.map((line) => <span key={line}>{line}<br /></span>)}{s.schedule_source === "connecteam" && <small>Connecteam · {s.schedule_window}</small>}</>, changed.has("schedule_lines")], ["Caseload", s.caseload.length ? `${s.caseload.join(", ")} (student initials only)` : "", changed.has("caseload")], ["Supervisor", contact(s.supervisor), changed.has("supervisor_name")]]} />
          </section>
          <section className="provider-assignment-section"><h2>Where to report</h2>
            <Rows testId="provider-assignment-site" rows={[["Address", s.profile.address, changed.has("location")], ["Reporting location", s.profile.reporting_location, changed.has("location")], ...Object.entries(s.contacts).map(([key, c]) => [page.contact_labels[key], contact(c)])]} />
          </section>
          <section className="provider-assignment-section"><h2>Arrival, access and site rules</h2>
            <Rows testId="provider-assignment-access" rows={[["Arrival", s.profile.arrival_instructions, changed.has("access")], ["Access", s.profile.access_instructions, changed.has("access")], ["Military base access", s.profile.military_base_access, changed.has("access")], ["Parking", s.profile.parking, changed.has("access")], ["Dress code", s.profile.dress_code], ["ID and visitors", s.profile.id_visitor_rules], ["Emergencies", s.profile.emergency_procedures]]} />
          </section>
          <section className="provider-assignment-section" data-testid="provider-assignment-documents"><h2>Client documents</h2>
            {page.documents.length === 0 && <p className="muted">No documents for this client yet.</p>}
            {page.documents.map((document) => (
              <article className={`provider-doc ${document.acknowledged ? "is-acknowledged" : "is-pending"}`} data-testid={`provider-doc-${document.id}`} key={document.id}>
                <div><strong>{document.title}</strong><span>v{document.version} · dated {document.version_date} · {document.category}{document.note ? ` · ${document.note}` : ""}</span>{document.previous_acknowledged_version && <em>New version since you acknowledged v{document.previous_acknowledged_version}</em>}</div>
                <div className="provider-doc-actions">
                  <button className="button button-secondary" data-testid={`provider-doc-download-${document.id}`} type="button" onClick={() => download(document.id, document.filename)}><Download size={14} /> Open</button>
                  {document.acknowledged ? <small data-testid={`provider-doc-acked-${document.id}`}><FileCheck2 size={14} /> Read {new Date(document.acknowledged_at).toLocaleDateString()}</small> : <button className="primary-button" data-testid={`provider-doc-ack-${document.id}`} type="button" onClick={() => acknowledge(document)}>I have read this</button>}
                </div>
              </article>
            ))}
          </section>
          <section className="provider-assignment-section"><h2>Connecteam</h2>
            <Rows testId="provider-assignment-connecteam" rows={[["Clock in and out", s.clock_in], ["Absence or lateness", s.absences]]} />
          </section>
          <section className="provider-assignment-section"><h2>Your TAG contacts</h2>
            <Rows testId="provider-assignment-contacts" rows={[["Staffing coordinator", contact(s.coordinator)], ["TAG supervisor", contact(s.supervisor)]]} />
          </section>
          {s.sections.map((section) => <section className="provider-assignment-section" key={section.title}><h2>{section.title}</h2><p>{section.body}</p></section>)}
          {s.notes && <section className="provider-assignment-section"><h2>From your coordinator</h2><p className={changed.has("notes") ? "is-changed" : ""}>{s.notes}</p></section>}
          <section className="provider-assignment-confidentiality" data-testid="provider-assignment-confidentiality"><ShieldCheck size={16} /><p><strong>Confidentiality reminder.</strong> {page.confidentiality}</p></section>
          <p className="provider-assignment-closing">{s.closing}</p>
          <p className="muted" data-testid="provider-assignment-sent">Sent {new Date(page.sent_at).toLocaleString()} by {page.sent_by_name} · version {page.version}</p>
        </>
      )}
    </ProviderShell>
  );
}
